import { find, get, isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoDuplicateOutline } from "react-icons/io5";
import { MdArrowBackIos, MdArrowForwardIos } from "react-icons/md";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Loading from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";
import CombinationCard from "./Widgets/InsurancePlans/CombinationCard";
import DownloadOrSend from "./Widgets/DownloadOrSend";
import InsurancePlanCard from "./Widgets/InsurancePlans";

const View = ({
  project,
  onSubscribe,
  onDownload,
  onSendQuote,
  data,
  onBack,
  loading,
  openModal,
  setOpenModal,
  duplicateProject,
  hideDetail,
  setHideDetail,
}) => {
  const { locked, fields, subscriptions, contract } = project;
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const [selectedModules, setSelectedModules] = useState(
    project?.fields?.upsells || [],
  );
  const DETAIL = t("DETAIL", { returnObjects: true });

  const persistedCombinations = get(project, "fields.combinations", []);
  const upsellsByBase = get(project, "fields.upsells", {});
  const selectedCombinationId = get(project, "fields.selectedCombinationId");

  const enrichedCombinations = useMemo(() => {
    if (isEmpty(persistedCombinations)) return [];
    return persistedCombinations
      .map((combination) => {
        const baseGeneralCost = find(
          data,
          (gc) =>
            get(gc, "insurancePlan.id") === combination.baseInsurancePlanId,
        );
        if (!baseGeneralCost) return null;
        const baseInsurancePlan = get(baseGeneralCost, "insurancePlan");
        const baseCost = get(baseGeneralCost, "cost", 0);
        const currency = get(baseGeneralCost, "currency");
        const complementModule = combination.complementaryInsurancePlanId
          ? find(
              get(upsellsByBase, combination.baseInsurancePlanId, []),
              (module) =>
                get(module, "insurancePlan.id") ===
                combination.complementaryInsurancePlanId,
            )
          : null;
        const complementaryInsurancePlan = get(
          complementModule,
          "insurancePlan",
        );
        const complementCost = get(complementModule, "cost", 0);
        const baseName = get(baseInsurancePlan, "name", "");
        const complementName = get(complementaryInsurancePlan, "name");
        return {
          ...combination,
          baseInsurancePlan,
          complementaryInsurancePlan,
          baseCost,
          complementCost,
          totalCost: baseCost + complementCost,
          currency,
          label: complementName ? `${baseName} avec ${complementName}` : baseName,
        };
      })
      .filter(Boolean);
  }, [persistedCombinations, data, upsellsByBase]);

  const showCombinations = locked && enrichedCombinations.length > 0;
  const subscribeFromCombination = ({ combination, subscription }) =>
    onSubscribe({
      combination,
      actionsTemplate: {
        id: subscription?.id,
        status: subscription?.status,
      },
    });

  return (
    <div>
      <Header
        key={"header"}
        title={DETAIL.TEMPLATES.COMPARISON.TITLE_LMF}
        actions={[]}
      />
      <div className="comparison-container">
        <div className="d-flex justify--end">
          {locked && project?.expired ? (
            <>
              <Button
                onClick={() => duplicateProject()}
                type="primary--outlined"
              >
                <IoDuplicateOutline size={16} />
                {DETAIL.TEMPLATES.COMPARISON.DUPLICATE_PROJECT}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setOpenModal(true)}
              type="primary--outlined"
              disabled={project?.expired}
            >
              {DETAIL.TEMPLATES.COMPARISON.NEXT}
              <MdArrowForwardIos size={16} />
            </Button>
          )}
        </div>
        <Card
          type="custom"
          size="full"
          title={
            showCombinations
              ? DETAIL.TEMPLATES.COMPARISON.CARD_TITLE_COMBINATIONS_LMF
              : DETAIL.TEMPLATES.COMPARISON.CARD_TITLE_LMF
          }
          subtitle={
            showCombinations
              ? DETAIL.TEMPLATES.COMPARISON.CARD_SUBTITLE_COMBINATIONS_LMF
              : DETAIL.TEMPLATES.COMPARISON.CARD_SUBTITLE_LMF
          }
          head
        >
          <DownloadOrSend
            data={data}
            open={openModal}
            onClose={() => setOpenModal(false)}
            onDownload={onDownload}
            onSendQuote={onSendQuote}
            fields={fields}
            insurancePlans={get(project, "subscriptions", []).map(
              ({ insurancePlan }) => insurancePlan?.id,
            )}
            selectedModules={selectedModules}
            project={project}
          />

          {loading ? (
            <Loading />
          ) : showCombinations ? (
            <div className="insurance-plans-grid">
              {enrichedCombinations.map((combination) => {
                const subscription = find(
                  subscriptions,
                  (sub) =>
                    get(sub, "insurancePlan.id") ===
                    combination.baseInsurancePlanId,
                );
                const isSelected =
                  selectedCombinationId === combination.id ||
                  (!selectedCombinationId && enrichedCombinations.length === 1);
                return (
                  <CombinationCard
                    key={combination.id}
                    combination={combination}
                    selected={isSelected}
                    translation={DETAIL.TEMPLATES.COMPARISON}
                    action={{
                      label: DETAIL.TEMPLATES.COMPARISON.SUBSCRIPTION,
                      disabled: !subscription || project.expired,
                      onClick: () =>
                        subscribeFromCombination({
                          combination,
                          subscription,
                        }),
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <div className="insurance-plans-grid">
              {data?.map(({ insurancePlan, cost, currency }) => (
                <InsurancePlanCard
                  key={insurancePlan.id}
                  insurancePlan={insurancePlan}
                  subscriptions={subscriptions}
                  issuanceDate={get(contract, "issuanceDate")}
                  isRIA={get(project, "ria", false)}
                  cost={cost}
                  currency={currency}
                  translation={DETAIL.TEMPLATES.COMPARISON}
                  onSubscribe={onSubscribe}
                  expired={project.expired}
                  locked={project.locked}
                  selectedModules={selectedModules}
                  setSelectedModules={setSelectedModules}
                  project={project}
                  hideSubscribe
                />
              ))}
            </div>
          )}
        </Card>

        <Button
          onClick={onBack}
          type="secondary--link"
          disabled={project.locked}
        >
          <MdArrowBackIos size={16} />
          {DETAIL.TEMPLATES.COMPARISON.PREVIOUS}
        </Button>
      </div>
    </div>
  );
};

export default View;
